import React, { ReactNode, useCallback, useState } from 'react';

import { SelfServiceSMBSignupModal } from '@/auth/components/SelfServiceSMBSignupModal';
import { useSelfServiceSMBSignupModal } from '@/auth/components/SelfServiceSMBSignupModal/hooks/useSelfServiceSMBSignupModal';
import { useIsSMBIncomplete } from '@/auth/hooks/useIsSMBIncomplete';
import { useAppSelector } from '@/store';

import TeamAccountPromoDialog from './TeamAccountPromoDialog';

/**
 * ENG-5251 (PR #2688 review S1 + S2): shared 3-branch logic for the
 * "Find Experts" / "New Expert Request" CTA. Both desktop button
 * (`TeamAccountPromoButton`) and mobile nav-menu item (`AccessHeader`)
 * route through this hook so behavior stays in lockstep:
 *
 *  - `smb-signup`    — viewer is an SMB signup who hasn't completed billing;
 *                      open the SelfServiceSMBSignupModal so they can finish.
 *  - `navigate`      — viewer has ≥1 group with billing; render a real
 *                      `<Link to={to}>` so cmd/middle-click open in a new tab.
 *  - `contact-sales` — non-SMB viewer with no groups (rare: experts without
 *                      an internal team); open the Contact Sales dialog.
 *
 * Pre-fix the mobile nav-menu skipped the contact-sales branch (only had
 * SMB-or-navigate), leaving non-SMB no-groups viewers with a broken
 * `/request_expert` page on mobile vs. Contact Sales on desktop.
 *
 * The hook exposes the resolved branch and a `surface` ReactNode that the
 * consumer renders once. Render decisions (anchor vs button) are the
 * consumer's responsibility — the hook only returns enough state for them
 * to do the right thing.
 */

type Branch = 'smb-signup' | 'navigate' | 'contact-sales';

interface UseFindExpertsActionOptions {
  to?: string;
  // Loose typing because TeamAccountPromoDialog is `(props: any)` (deferred
  // cleanup) — we don't want to widen its surface here.
  dialogProps?: Record<string, unknown>;
}

interface UseFindExpertsActionResult {
  /** Resolved branch — drives the consumer's render decision. */
  branch: Branch;
  /** Destination when `branch === 'navigate'`. Undefined otherwise. */
  to?: string;
  /** Open the SMB signup modal. No-op unless `branch === 'smb-signup'`. */
  openSignup: () => void;
  /** Open the Contact Sales dialog. No-op unless `branch === 'contact-sales'`. */
  openContactSales: () => void;
  /**
   * Lazy-mounted modal + dialog surface. Render this once at the consumer
   * level — the React tree owns the open/close state; the elements are
   * inert until `openSignup` / `openContactSales` are called.
   */
  surface: ReactNode;
}

const DEFAULT_TO = '/request_expert';

// Lazy-mounted modal surface. Mounting `useSelfServiceSMBSignupModal`
// unconditionally would run viewer queries on every render of the
// consumer (experts, partner customers, etc.) for work they never trigger.
// Pulled into a child so the hook can mount it only when the SMB branch
// is active. Mirrors the previous lazy-mount discipline that was
// duplicated across AccessHeader and TeamAccountPromoButton.
function SMBSignupSurface({ open, onClose }: { open: boolean; onClose: () => void }) {
  // skipHasTeamBillingRedirect: this CTA is itself a valid destination
  // for a freshly-signed-up viewer; don't let the hook's "viewer already
  // has billing" guard bounce them away from the page they're on.
  const { modalProps } = useSelfServiceSMBSignupModal({
    skipHasTeamBillingRedirect: true,
    onClose,
  });
  return <SelfServiceSMBSignupModal {...modalProps} open={open} onClose={onClose} />;
}

export function useFindExpertsAction(
  opts: UseFindExpertsActionOptions = {}
): UseFindExpertsActionResult {
  const { to = DEFAULT_TO, dialogProps } = opts;
  const viewer = useAppSelector((state) => state.viewer);
  const { isSMBIncomplete } = useIsSMBIncomplete();

  const [signupOpen, setSignupOpen] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const openSignup = useCallback(() => setSignupOpen(true), []);
  const closeSignup = useCallback(() => setSignupOpen(false), []);
  const openContactSales = useCallback(() => setContactSalesOpen(true), []);
  const closeContactSales = useCallback(() => setContactSalesOpen(false), []);

  const hasGroups = !!viewer.groups && viewer.groups.length > 0;

  let branch: Branch;
  let navigateTo: string | undefined;
  if (isSMBIncomplete) {
    branch = 'smb-signup';
  } else if (hasGroups) {
    branch = 'navigate';
    navigateTo = to;
  } else {
    branch = 'contact-sales';
  }

  const surface = (
    <React.Fragment>
      {isSMBIncomplete && <SMBSignupSurface open={signupOpen} onClose={closeSignup} />}
      <TeamAccountPromoDialog
        dialogpProps={dialogProps}
        open={contactSalesOpen}
        onClose={closeContactSales}
      />
    </React.Fragment>
  );

  return {
    branch,
    to: navigateTo,
    openSignup,
    openContactSales,
    surface,
  };
}
